import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { FunnelIcon } from '@heroicons/react/24/outline';

const MergeNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const waitForAll = data.config?.waitForAll || false;
  const inputCount = data.config?.inputCount || 2;

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-teal-400 to-teal-500 border-2 transition-all duration-200 ${
      selected ? 'border-teal-600 shadow-xl scale-105' : 'border-teal-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <FunnelIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-teal-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Config preview */}
          <div className="text-xs text-teal-100 mt-1">
            {waitForAll ? 'Minden bemenet szükséges' : 'Első bemenet elég'}
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 
            data.executionData?.waiting ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution data preview */}
      {data.executionData && (
        <div className="mt-2 text-xs text-teal-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="font-mono">
            {data.executionData.waiting ? (
              <>Várakozás bemenetekre: {data.executionData.receivedInputs || 0}/{inputCount}</>
            ) : (
              <>Összes bemenet megérkezett</>
            )}
          </div>
        </div>
      )}

      {/* Multiple input handles */}
      {Array.from({ length: inputCount }, (_, index) => (
        <Handle
          key={`input-${index}`}
          type="target"
          position={Position.Left}
          id={`input-${index}`}
          className="w-3 h-3 bg-white border-2 border-teal-500"
          style={{ 
            left: -6, 
            top: `${50 + (index - Math.floor(inputCount / 2)) * 20}%` 
          }}
        />
      ))}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="w-3 h-3 bg-white border-2 border-teal-500"
        style={{ right: -6 }}
      />
    </div>
  );
};

export default MergeNode;
